import { ref, computed, unref, watch } from 'vue';
import debounce from 'lodash/debounce';
import bookingAPI from '@/services/bookingAPI';
import journeyAPI from '@/services/journeyAPI';
import { analizzaCatene, chiaveLuogo, MAX_PASSEGGERI, opzioniRientro as opzioniCatena, vistaSedi } from '@/utils/catene';
import { isLoggedStore } from '@/store/logged';

// ---------------------------------------------------------------------------
//  useJourneyLegs — TUTTA la logica di business delle Corse (Journey legs).
//
//  Regole di business preservate (NON alterare):
//   - Mission chaining: "Spostamento a 2 operatori" (ex "Auto da spostare") =
//     chaining manuale dalla partenza; "Spostamento a catena" (ex "Auto per
//     driver") = auto-append di una corsa dalla destinazione (Sede B) con
//     partenza BLOCCATA (autoChained).
//   - Smart transit: se Start(N) != End(N-1), trasferimento Google
//     End(N-1) -> Start(N); minimo di partenza = dateEnd(N-1) + transito.
//     (Il veicolo/driver riparte da DOVE È ARRIVATO, non da dove era partito.)
//   - Nessuna corsa passiva in UI: tutte le corse hanno movement:true.
//
//  Resilienza API:
//   - memo-cache di sessione per coppie di punti (dedup anche delle richieste
//     in-flight) -> niente chiamate ripetute per coppie identiche;
//   - seq-token per corsa -> le risposte stantie vengono scartate;
//   - commit-guard + debounce per campo -> niente doppio fuoco @select/@change.
// ---------------------------------------------------------------------------

let legUidSeq = 0;

const makeLeg = (overrides = {}) => {
  const leg = {
    uid: (legUidSeq += 1),
    sourceId: null, // id della Route persistita (edit mode); null se nuova
    startPoint: null,
    endPoint: null,
    startText: '',
    endText: '',
    dateStart: '',
    dateEnd: '',
    details: '',
    movement: true, // tutte le corse sono attive
    isShuttle: true, // DEFAULT "Spostamento singolo" (2 operatori)
    autoChained: false, // corsa auto-generata da "Spostamento a catena"
    chainedFrom: null,
    // Corsa a catena il cui driver rientra a bordo di un'altra corsa
    // (`rientroAltrove`): id di quella corsa ('u<uid>' in questo editor,
    // 'x<id>' di un altro spostamento), null se non è ancora scelto. A
    // catena non esiste «rientra in un altro modo».
    rientroSu: null,
    rientroSourceId: null, // id salvato della corsa di rientro (edit mode)
    andataSourceId: null, // id salvato della corsa con cui arriva a bordo (edit mode)
    // AUTO IN PIÙ sulla stessa corsa (stessa partenza, arrivo e orario), fino
    // a 5 in tutto. È solo una comodità di chi compila: al salvataggio ogni
    // auto diventa una corsa sua, con il suo driver. A catena i loro driver
    // rientrano a bordo della corsa con cui torna il driver della prima auto.
    altreAuto: [],
    marca: '',
    modello: '',
    targa: '',
    gearbox_type: '', // '' | 'MANUAL' | 'AUTOMATIC' (opzionale)
    // Veicolo senza immatricolazione: si sposta con la targa prova e ha
    // una tariffa sua. Scritto al negativo perché è l'eccezione, e la
    // casella deve nascere spenta.
    nonImmatricolato: false,
    transitMin: 0, // trasferimento End(N-1) -> Start(N)
    previewKm: null,
    previewMin: null,
    previewLoading: false,
    // Costo della corsa con le tariffe del partner (senza IVA).
    // previewPriceState: 'idle' (mancano km o data) | 'loading' | 'ok' |
    // 'missing' (nessuna tariffa per quella distanza) | 'error'.
    previewPrice: null,
    previewPriceState: 'idle',
    // Indirizzo geolocalizzato dal backend per gli estremi digitati a mano.
    startResolved: '',
    endResolved: '',
    // Perché km e minuti non ci sono: indirizzo non riconosciuto, oppure
    // errore del servizio mappe. Vuoto = nessun problema.
    previewError: '',
    // Disponibilità verificata dal backend (capacità della provincia di
    // partenza + driver liberi). state: 'idle' (corsa non ancora completa) |
    // 'loading' | 'ok' | 'unavailable' | 'error'. firstStart = primo orario
    // libero "HH:mm" (vuoto se in giornata non ce n'è).
    availability: { state: 'idle', reason: null, firstStart: '' },
    ...overrides,
  };
  // Guardie anti doppio-fuoco / risposte stantie (non reattive per la UI).
  leg._seq = 0;
  leg._priceSeq = 0;
  leg._lastStartCommit = leg.startText || '';
  leg._lastEndCommit = leg.endText || '';
  // Guardia di inserimento IN-LINE: se valorizzata, questa corsa è appena stata
  // inserita PRIMA della corsa `_guardNextUid`, di cui proteggiamo l'orario
  // originale `_guardNextStart` per rilevare lo sforamento.
  leg._guardNextUid = null;
  leg._guardNextStart = null;
  return leg;
};

// Massimo di auto su una corsa: il driver che guida il ritorno più i 4 che
// ci rientrano a bordo.
const MAX_AUTO = 5;
let autoUidSeq = 0;
const makeAuto = (overrides = {}) => ({
  uid: (autoUidSeq += 1),
  sourceId: null, // id della Route salvata (edit mode)
  marca: '',
  modello: '',
  targa: '',
  gearbox_type: '',
  nonImmatricolato: false,
  details: '', // note dell'auto
  // costo con le tariffe del partner (gestionale partner)
  previewPrice: null,
  previewPriceState: 'idle',
  ...overrides,
});

const normText = (s) => (s || '').trim().toLowerCase().replace(/\s+/g, ' ');

export function useJourneyLegs({
  initialRoutes = [],
  date,
  // Spostamento in modifica (ref o id): le sue tratte già salvate non
  // occupano la disponibilità mentre si verifica. null in creazione.
  journeyId = null,
  // Spostamenti dello stesso giorno già salvati (ref, con le corse): il
  // driver può rientrare a bordo di una loro corsa.
  giornata = null,
  // Callback UI (iniettato dal componente) mostrato quando un inserimento sfora
  // sugli orari successivi. Ritorna Promise<'shift' | 'cancel' | 'after'>:
  //  - 'shift'  : slitta in avanti le corse successive (default storico)
  //  - 'cancel' : annulla l'inserimento
  //  - 'after'  : sposta la nuova corsa DOPO quella in conflitto
  resolveOverlap = async () => 'shift',
} = {}) {
  const storeLogin = isLoggedStore();
  const headquartersCascader = ref([]);

  // ----- Seed ---------------------------------------------------------------
  const seeded =
    initialRoutes && initialRoutes.length
      ? initialRoutes.map((r) =>
          makeLeg({
            sourceId: r.sourceId || null,
            startPoint: r.startPoint || null,
            endPoint: r.endPoint || null,
            startText: r.startText || '',
            endText: r.endText || '',
            dateStart: r.dateStart || '',
            dateEnd: r.dateEnd || '',
            details: r.details || '',
            isShuttle: r.isShuttle === undefined ? true : !!r.isShuttle,
            autoChained: !!r.autoChained,
            rientroSourceId: r.rientroSourceId || null,
            andataSourceId: r.andataSourceId || null,
            marca: r.marca || '',
            modello: r.modello || '',
            targa: r.targa || '',
            gearbox_type: r.gearbox_type || '',
            nonImmatricolato: r.nonImmatricolato === true,
          })
        )
      : [makeLeg()];
  // In MODIFICA una corsa salvata tiene la durata con cui è stata salvata
  // finché non le si cambiano partenza o arrivo: le mappe, richieste di
  // nuovo, possono dare un minuto in più (08:06 diventava 08:07) e con il
  // margine la corsa finiva sovrapposta alla successiva, che risultava non
  // disponibile («la provincia ha già il massimo di corse insieme»).
  const estremiDi = (leg) => `${chiaveLuogo(leg.startPoint)}>${chiaveLuogo(leg.endPoint)}`;
  seeded.forEach((leg) => {
    if (!leg.sourceId || !leg.dateStart || !leg.dateEnd) return;
    const [sh, sm] = leg.dateStart.split(':').map(Number);
    const [eh, em] = leg.dateEnd.split(':').map(Number);
    const durata = eh * 60 + em - (sh * 60 + sm);
    if (durata > 0) {
      leg._durataSalvata = durata;
      leg._estremiSalvati = estremiDi(leg);
    }
  });
  for (let i = 1; i < seeded.length; i++) {
    if (seeded[i].autoChained) seeded[i].chainedFrom = seeded[i - 1].uid;
  }
  // Il rientro a bordo salvato punta a un id: nell'editor diventa la corsa
  // dell'editor, o una corsa di un altro spostamento.
  seeded.forEach((leg) => {
    if (!leg.rientroSourceId) return;
    const verso = seeded.find((r) => r.sourceId === leg.rientroSourceId);
    leg.rientroSu = verso ? `u${verso.uid}` : `x${leg.rientroSourceId}`;
  });
  // Spostamento già salvato: le auto in più di una corsa sono corse uguali
  // (stessa partenza, arrivo, orari e tipo) i cui driver tornano con la
  // stessa corsa del driver della prima — o, singolo, ognuna col suo jolly.
  // Nell'editor tornano una corsa sola con più auto.
  const raggruppaAuto = (legs) => {
    const min = (hhmm) => {
      const [h, m] = String(hhmm || '').split(':').map(Number);
      return Number.isFinite(h) ? h * 60 + (m || 0) : null;
    };
    const perSource = new Map(legs.filter((l) => l.sourceId).map((l) => [l.sourceId, l]));
    const a = analizzaCatene(legs.map((l) => ({
      id: `s${l.uid}`,
      da: chiaveLuogo(l.startPoint),
      a: chiaveLuogo(l.endPoint),
      inizio: min(l.dateStart),
      fine: min(l.dateEnd),
      catena: l.isShuttle === false,
      rientro: l.rientroSourceId
        ? (perSource.get(l.rientroSourceId) ? `s${perSource.get(l.rientroSourceId).uid}` : `x${l.rientroSourceId}`)
        : null,
      andata: l.andataSourceId && perSource.get(l.andataSourceId) ? `s${perSource.get(l.andataSourceId).uid}` : null,
    })));
    const chiave = (l) =>
      [chiaveLuogo(l.startPoint), chiaveLuogo(l.endPoint), l.dateStart, l.dateEnd, l.isShuttle ? 's' : 'c'].join('|');
    const ritornoDi = (l) => {
      if (l.isShuttle) return null;
      if (l.rientroSourceId) return l.rientroSourceId;
      const f = a.figlio.get(`s${l.uid}`);
      return f ? legs.find((x) => `s${x.uid}` === f)?.sourceId || null : null;
    };
    // Da quale corsa riparte e in quale prosegue (per raggruppare le auto
    // anche quando al ritorno ognuno ne riporta una).
    const legDi = (sid) => legs.find((x) => `s${x.uid}` === sid);
    const firma = (l) => {
      const p = legDi(a.padre.get(`s${l.uid}`));
      const f = legDi(a.figlio.get(`s${l.uid}`));
      return `${p ? chiave(p) : ''}#${f ? chiave(f) : ''}`;
    };
    // La prima auto è quella il cui driver guida il ritorno (o, singolo, la
    // prima): le altre si mettono sotto di lei. L'ordine delle corse resta.
    const out = [];
    const usate = new Set();
    for (const l of legs) {
      if (usate.has(l)) continue;
      let capo = l;
      if (!l.autoChained && l.sourceId) {
        const pari = legs.filter((x) => !usate.has(x) && !x.autoChained && x.sourceId && chiave(x) === chiave(l));
        // Chi è arrivato a bordo non guida la prima auto: la guida chi è
        // arrivato guidando.
        capo = pari.find((x) => (x.isShuttle || !x.rientroSourceId) && !x.andataSourceId)
          || pari.find((x) => x.isShuttle || !x.rientroSourceId) || l;
        const padreDi = (x) => legDi(a.padre.get(`s${x.uid}`))?.sourceId || null;
        for (const x of pari) {
          if (x === capo || capo.altreAuto.length >= MAX_AUTO - 1) continue;
          const stessa = x.isShuttle
            ? capo.isShuttle
            // Andata a bordo (1 auto A → B, 2 B → A): l'auto in più è della
            // corsa che riparte da quella con cui è arrivato il suo driver.
            : x.andataSourceId
              ? padreDi(capo) === x.andataSourceId && (!x.rientroSourceId || x.rientroSourceId === ritornoDi(capo))
            : x.rientroSourceId
              ? x.rientroSourceId === ritornoDi(capo)
              // Più auto anche dopo (2 auto A → B, 2 B → A): la stessa
              // corsa se vengono dalla stessa e proseguono nella stessa.
              : !capo.rientroSourceId && firma(x) === firma(capo);
          if (!stessa) continue;
          capo.altreAuto.push(makeAuto({
            sourceId: x.sourceId,
            marca: x.marca,
            modello: x.modello,
            targa: x.targa,
            gearbox_type: x.gearbox_type,
            nonImmatricolato: x.nonImmatricolato,
            details: x.details,
          }));
          usate.add(x);
        }
      }
      usate.add(capo);
      out.push(capo);
    }
    return out;
  };
  const routes = ref(raggruppaAuto(seeded));

  // In MODIFICA la corsa che prosegue la catena della precedente torna
  // «legata», come nella creazione: parte da dove arriva l'altra e ne segue
  // gli orari (spostando l'andata, il ritorno si sposta). Senza, riaperto lo
  // spostamento, il ritorno restava fermo e si sovrapponeva all'andata.
  (() => {
    const legs = routes.value;
    if (!legs.some((l) => l.sourceId)) return;
    const min = (hhmm) => {
      const [h, m] = String(hhmm || '').split(':').map(Number);
      return Number.isFinite(h) ? h * 60 + (m || 0) : null;
    };
    const a = analizzaCatene(legs.map((l) => ({
      id: `s${l.uid}`,
      da: chiaveLuogo(l.startPoint),
      a: chiaveLuogo(l.endPoint),
      inizio: min(l.dateStart),
      fine: min(l.dateEnd),
      catena: l.isShuttle === false,
      // chi rientra a bordo non prosegue niente
      rientro: l.rientroSourceId ? 'x' : null,
    })));
    for (let i = 1; i < legs.length; i++) {
      if (a.padre.get(`s${legs[i].uid}`) === `s${legs[i - 1].uid}`) {
        legs[i].autoChained = true;
        legs[i].chainedFrom = legs[i - 1].uid;
      }
    }
  })();

  // ----- Sedi (autocomplete) --------------------------------------------------
  // Le sedi che si possono scegliere come PARTENZA o ARRIVO: tutte quelle del
  // partner, non solo quelle dove lavora chi compila. Una corsa può portare
  // un'auto alla sede di un collega; la visibilità per sede riguarda le corse
  // (cosa si vede), non le destinazioni (dove si può andare).
  const loadHeadquarters = async () => {
    try {
      const response = await bookingAPI.getHeadquartersOfPartner();
      const sedi = response.data?.data ?? response.data ?? [];
      sedi.forEach((headquarter) => {
        // Etichetta con il nome commerciale (es. "Audi Ancona — Via Roma 10 Ancona"),
        // fallback al solo indirizzo per le sedi senza nome.
        const addressLabel = `${headquarter.address} ${headquarter.street_number} ${headquarter.city}`;
        headquartersCascader.value.push({
          value: headquarter.id,
          label: headquarter.name
            ? `${headquarter.name} — ${addressLabel}`
            : addressLabel,
          point: {
            id: headquarter.id,
            address: headquarter.address,
            street_number: headquarter.street_number,
            cap: headquarter.cap,
            city: headquarter.city,
            province: headquarter.province,
            latitude: headquarter.latitude,
            longitude: headquarter.longitude,
          },
        });
      });
    } catch (error) {
      console.error('Errore nel caricamento degli headquarters:', error.message);
    }
  };
  loadHeadquarters();

  const querySuggestions = (queryString, cb) => {
    const list = (headquartersCascader.value || []).map((o) => ({
      value: o.label,
      point: o.point,
    }));
    const q = (queryString || '').toLowerCase();
    cb(q ? list.filter((i) => i.value.toLowerCase().includes(q)) : list);
  };

  const resolvePoint = (val) => {
    const text = (val || '').trim();
    if (!text) return null;
    const hq = (headquartersCascader.value || []).find((o) => o.label === text);
    if (hq) return { ...hq.point };
    return { address: text, street_number: '', cap: '', city: '', province: '' };
  };

  const pointToDto = (p) => ({
    headquarter_id: p?.id,
    address: p?.address || '',
    street_number: p?.street_number || '',
    cap: p?.cap || '',
    city: p?.city || '',
    province: p?.province || '',
    latitude: p?.latitude,
    longitude: p?.longitude,
  });

  const samePoint = (a, b) => {
    if (!a || !b) return false;
    if (a.id != null && b.id != null) return a.id === b.id;
    return (
      normText(a.address) === normText(b.address) &&
      normText(a.city) === normText(b.city)
    );
  };

  // ----- Geocoding indirizzi liberi (memo + dedup in-flight) -------------------
  // Ogni estremo digitato a mano viene geolocalizzato APPENA committato,
  // indipendentemente dall'altro estremo della tratta: il punto viene
  // sostituito con la versione strutturata e l'indirizzo formattato è
  // mostrato all'utente come conferma.
  const geocodeMemo = new Map();
  const geocodeFreeText = (p) => {
    const key = normText(p?.address);
    if (!key) return Promise.resolve(null);
    if (geocodeMemo.has(key)) return geocodeMemo.get(key);
    const promise = journeyAPI
      .geocodePoint(pointToDto(p))
      .then((resp) => resp.data || null)
      .catch(() => {
        geocodeMemo.delete(key); // consenti il retry dopo un errore
        return null;
      });
    geocodeMemo.set(key, promise);
    return promise;
  };

  // ----- Memo-cache coppie (con dedup delle richieste in-flight) --------------
  const pairMemo = new Map();
  const pointKey = (p) =>
    p?.id != null ? `hq:${p.id}` : `tx:${normText(p?.address)}|${normText(p?.city)}`;
  const fetchPair = (a, b) => {
    const key = `${pointKey(a)}>${pointKey(b)}`;
    if (pairMemo.has(key)) return pairMemo.get(key);
    const promise = journeyAPI
      .previewLeg(pointToDto(a), pointToDto(b))
      .then((resp) => ({
        km: typeof resp.data?.km === 'number' ? resp.data.km : null,
        min: typeof resp.data?.min === 'number' ? resp.data.min : null,
      }))
      .catch((e) => {
        pairMemo.delete(key); // consenti il retry dopo un errore
        throw e;
      });
    pairMemo.set(key, promise);
    return promise;
  };

  // ----- Costo della corsa (tariffe DEL PARTNER) ---------------------------------
  // Stesso calcolo di `GET /journey/:id/partner-price` sul dettaglio: fascia
  // del partner (`share: true`) per km, data, numero di operatori e
  // immatricolazione del veicolo (la targa prova ha un listino suo). Memo per
  // chiave, così cambiare orari o note non rilancia la richiesta.
  const priceMemo = new Map();
  const fetchPrice = (km, day, operators, immatricolato) => {
    const key = `${km}|${day}|${operators}|${immatricolato}`;
    if (priceMemo.has(key)) return priceMemo.get(key);
    const promise = journeyAPI
      .quotePartner(km, day, operators, immatricolato)
      .then((resp) => (typeof resp.data?.price === 'number' ? resp.data.price : null))
      .catch((e) => {
        priceMemo.delete(key); // consenti il retry dopo un errore
        throw e;
      });
    priceMemo.set(key, promise);
    return promise;
  };

  const quoteLeg = async (leg) => {
    if (!leg) return;
    const mySeq = ++leg._priceSeq;
    const day = unref(date) || '';
    if (typeof leg.previewKm !== 'number' || !/^\d{4}-\d{2}-\d{2}$/.test(day)) {
      leg.previewPrice = null;
      leg.previewPriceState = 'idle';
      return;
    }
    leg.previewPriceState = 'loading';
    try {
      const price = await fetchPrice(
        leg.previewKm,
        day,
        leg.isShuttle ? 2 : 1,
        !leg.nonImmatricolato
      );
      if (mySeq !== leg._priceSeq) return;
      leg.previewPrice = price;
      leg.previewPriceState = price === null ? 'missing' : 'ok';
      // Le auto in più: stessa corsa, ma la targa prova può cambiare listino.
      for (const auto of leg.altreAuto || []) {
        const p = await fetchPrice(leg.previewKm, day, leg.isShuttle ? 2 : 1, !auto.nonImmatricolato);
        if (mySeq !== leg._priceSeq) return;
        auto.previewPrice = p;
        auto.previewPriceState = p === null ? 'missing' : 'ok';
      }
    } catch (e) {
      if (mySeq !== leg._priceSeq) return;
      // La chiamata non mostra toast (skipErrorToast): senza questo log non
      // resterebbe traccia del perché il costo manca.
      console.warn(
        'Costo corsa non calcolato:',
        e?.response?.status,
        e?.response?.data?.message || e?.message
      );
      leg.previewPrice = null;
      leg.previewPriceState = 'error';
    }
  };

  // ----- Matematica orari ------------------------------------------------------
  // ⚠️ Lo spostamento è di UN giorno: un orario che passerebbe la mezzanotte
  // (in avanti o indietro) non esiste, e torna ''. Prima l'orologio girava
  // senza dirlo: 23:30 + 21 min diventava 23:51, la corsa dopo 23:56, e
  // un arrivo «00:10» finiva prima della partenza.
  const addMinutesToTime = (hhmm, mins) => {
    if (!hhmm || mins === null || mins === undefined) return '';
    const [h, m] = hhmm.split(':').map(Number);
    if (!Number.isFinite(h) || !Number.isFinite(m)) return '';
    const tot = h * 60 + m + Math.round(mins);
    if (tot < 0 || tot >= 24 * 60) return '';
    return `${String(Math.floor(tot / 60)).padStart(2, '0')}:${String(tot % 60).padStart(2, '0')}`;
  };
  // Partenza nota, durata nota, ma l'arrivo cadrebbe dopo mezzanotte.
  const oltreMezzanotte = (leg) =>
    !!leg?.dateStart && leg.previewMin !== null && leg.previewMin !== undefined && !leg.dateEnd;

  // Minimo di partenza per la corsa i = ARRIVO della precedente + trasferimento
  // (End(N-1) -> Start(N)): il mezzo/driver riparte da dove è arrivato.
  // ⚠️ MARGINE fra una corsa e la successiva: 5 minuti, gli stessi che il
  // backend aggiunge alla fine di ogni corsa quando conta chi è occupato
  // (`CAPACITY_MARGIN_MINUTES`). Senza, una corsa a catena partiva ESATTAMENTE
  // all'arrivo della precedente e la verifica la rifiutava: l'operatore
  // risultava ancora impegnato. Vale per tutte le corse in fila, non solo per
  // quelle a catena: quando il trasferimento è più lungo il margine è già
  // compreso, quindi si prende il maggiore dei due.
  const MARGINE_MIN = 5;

  const floorStart = (index, transitMin) => {
    if (index <= 0) return '';
    const prevEnd = routes.value[index - 1].dateEnd;
    if (!prevEnd) return '';
    return addMinutesToTime(prevEnd, Math.max(transitMin || 0, MARGINE_MIN));
  };

  // Arrivo = partenza + minuti Google. Cascata domino che preserva i buffer.
  // L'orario di partenza vuoto viene proposto automaticamente = primo slot
  // disponibile (arrivo precedente + trasferimento).
  // Solo il RIENTRO (corsa auto-generata dopo una corsa a catena) è legato
  // alla corsa da cui parte: lo guida lo stesso driver, che prima deve
  // arrivarci. Le altre corse possono anche partire insieme, con driver
  // diversi: l'orario dopo la precedente è solo PROPOSTO, e se due corse si
  // sovrappongono decide la verifica di disponibilità (servono due driver).
  const vincolata = (leg) => !!leg?.autoChained;

  // RITORNO CON PASSEGGERI: la corsa su cui rientrano a bordo i driver di
  // altre corse parte solo quando sono arrivati tutti (+5 min). Spostando
  // una di quelle corse più tardi, il ritorno la segue (anche se nell'elenco
  // sta prima); spostandola prima, il ritorno resta dov'è.
  const MARGINE_BORDO = 5;
  const floorPasseggeri = (leg) => {
    if (!leg) return '';
    const id = `u${leg.uid}`;
    let floor = '';
    for (const r of routes.value) {
      if (r === leg || r.isShuttle !== false || !r.rientroAltrove || r.rientroSu !== id || !r.dateEnd) continue;
      const f = addMinutesToTime(r.dateEnd, MARGINE_BORDO);
      if (f > floor) floor = f;
    }
    return floor;
  };
  // Il primo orario possibile di una corsa: dopo la corsa da cui riparte lo
  // stesso driver (rientro generato) e dopo l'arrivo dei suoi passeggeri.
  const minimoPartenza = (index) => {
    const leg = routes.value[index];
    const dopoPrecedente = vincolata(leg) ? floorStart(index, leg?.transitMin || 0) : '';
    const dopoPasseggeri = floorPasseggeri(leg);
    return dopoPrecedente > dopoPasseggeri ? dopoPrecedente : dopoPasseggeri;
  };

  const cascata = (route_, index) => {
    const minStart = floorStart(index, route_.transitMin);
    if (minStart && (!route_.dateStart || (vincolata(route_) && route_.dateStart < minStart))) {
      route_.dateStart = minStart;
    }
    const bordo = floorPasseggeri(route_);
    if (bordo && route_.dateStart && route_.dateStart < bordo) route_.dateStart = bordo;
    route_.dateEnd =
      route_.dateStart &&
      route_.previewMin !== null &&
      route_.previewMin !== undefined
        ? addMinutesToTime(route_.dateStart, route_.previewMin)
        : '';
    const next = index + 1;
    if (next < routes.value.length) {
      const nextLeg = routes.value[next];
      const nextMin = route_.dateEnd
        ? addMinutesToTime(route_.dateEnd, Math.max(nextLeg.transitMin || 0, MARGINE_MIN))
        : '';
      if (nextMin && (!nextLeg.dateStart || (vincolata(nextLeg) && nextLeg.dateStart < nextMin))) {
        nextLeg.dateStart = nextMin;
      }
      cascata(nextLeg, next);
    }
  };

  // Dopo la cascata in ordine di elenco: i ritorni rimasti prima dell'arrivo
  // dei loro passeggeri si spostano, e da lì riparte la cascata.
  const allineaRitorni = () => {
    for (let giro = 0; giro < routes.value.length; giro++) {
      const i = routes.value.findIndex((leg) => {
        const f = floorPasseggeri(leg);
        return f && leg.dateStart && leg.dateStart < f;
      });
      if (i === -1) return;
      cascata(routes.value[i], i);
    }
  };

  // ANDATE CHE VIAGGIANO INSIEME: chi va a prendere un ritorno (il driver che
  // lo guida) e chi ci rientra a bordo partono insieme. Spostando l'orario di
  // una di queste andate, le altre e il ritorno si spostano degli stessi
  // minuti; il ritorno comunque mai prima degli arrivi (allineaRitorni).
  const minutiDi = (hhmm) => {
    if (!hhmm) return null;
    const [h, m] = String(hhmm).split(':').map(Number);
    return Number.isFinite(h) ? h * 60 + (m || 0) : null;
  };
  const ritornoDi = (leg) => {
    const a = analisi.value;
    const id = `u${leg.uid}`;
    const ritorno = leg.isShuttle === false && leg.rientroAltrove && String(leg.rientroSu || '').startsWith('u')
      ? leg.rientroSu
      : a.figlio.get(id);
    if (!ritorno || !(a.passeggeri.get(ritorno) || []).length) return null;
    return routes.value.find((r) => `u${r.uid}` === ritorno) || null;
  };
  const compagniDiViaggio = (leg) => {
    const a = analisi.value;
    const id = `u${leg.uid}`;
    // il ritorno a cui porta questa andata: da passeggero, o da driver
    const ritorno = leg.isShuttle === false && leg.rientroAltrove && String(leg.rientroSu || '').startsWith('u')
      ? leg.rientroSu
      : a.figlio.get(id);
    if (!ritorno) return [];
    const passeggeri = a.passeggeri.get(ritorno) || [];
    if (!passeggeri.length) return [];
    const ids = [a.padre.get(ritorno), ...passeggeri].filter((x) => x && x !== id);
    return routes.value.filter((r) => ids.includes(`u${r.uid}`));
  };

  const calculateArrivalTime = (route_, index) => {
    const prima = minutiDi(route_._ultimaPartenza);
    const dopo = minutiDi(route_.dateStart);
    // Le corse spostate insieme a questa: arrivo e cascata si rifanno anche
    // per loro, ovunque stiano nell'elenco.
    const spostate = [];
    if (prima !== null && dopo !== null && dopo !== prima) {
      const ritorno = ritornoDi(route_);
      for (const leg of [...compagniDiViaggio(route_), ritorno].filter(Boolean)) {
        if (!leg.dateStart) continue;
        // Fuori dalla giornata no: la corsa resta dov'era.
        const t = addMinutesToTime(leg.dateStart, dopo - prima);
        if (!t) continue;
        leg.dateStart = t;
        spostate.push(leg);
      }
    }
    // Prima gli arrivi di tutte: il ritorno si misura su quelli nuovi.
    for (const leg of [route_, ...spostate]) {
      leg.dateEnd =
        leg.dateStart && leg.previewMin !== null && leg.previewMin !== undefined
          ? addMinutesToTime(leg.dateStart, leg.previewMin)
          : leg.dateEnd;
    }
    cascata(route_, index);
    for (const leg of spostate) cascata(leg, routes.value.indexOf(leg));
    allineaRitorni();
    // L'orario di ognuna, per riconoscere il prossimo spostamento.
    routes.value.forEach((r) => {
      r._ultimaPartenza = r.dateStart;
    });
  };

  const getDisabledHours = (index) => {
    const floor = minimoPartenza(index);
    if (!floor) return [];
    const [fh] = floor.split(':').map(Number);
    const hours = [];
    for (let h = 0; h < fh; h++) hours.push(h);
    return hours;
  };
  const getDisabledMinutes = (index, hour) => {
    const floor = minimoPartenza(index);
    if (!floor) return [];
    const [fh, fm] = floor.split(':').map(Number);
    if (hour === fh) {
      const mins = [];
      for (let m = 0; m < fm; m++) mins.push(m);
      return mins;
    }
    return [];
  };

  // Valore di default del time-picker: il primo slot DISPONIBILE (floor della
  // corsa), NON l'ora corrente — evita di dover cancellare l'orario "adesso".
  const defaultStartTime = (index) => {
    const floor = floorStart(index, routes.value[index]?.transitMin || 0);
    const base = routes.value[index]?.dateStart || floor || '08:00';
    const [h, m] = base.split(':').map(Number);
    return new Date(2000, 0, 1, h || 8, m || 0, 0);
  };

  // ----- Motore di ricalcolo (scoped + seq-guard) ------------------------------
  // Calcola transito + anteprima per UNA corsa; scarta le risposte stantie.
  const applyPreview = async (leg, index) => {
    if (!leg) return;
    const mySeq = ++leg._seq;

    // Geocoding indipendente degli estremi LIBERI (prima di transiti e
    // anteprime): il punto viene sostituito con la versione strutturata dal
    // backend e l'indirizzo formattato è mostrato come conferma. Le sedi
    // (id presente) non vengono toccate.
    const geocodeEndpoint = async (pointField, resolvedField) => {
      const p = leg[pointField];
      if (!p || p.id != null) {
        leg[resolvedField] = '';
        return;
      }
      if (p.cap && p.city) return; // già strutturato in un giro precedente
      const g = await geocodeFreeText(p);
      if (mySeq !== leg._seq) return;
      if (g) {
        leg[pointField] = {
          address: g.address,
          street_number: g.street_number,
          cap: g.cap,
          city: g.city,
          province: g.province,
          latitude: g.latitude,
          longitude: g.longitude,
        };
        leg[resolvedField] = g.formatted || '';
      } else {
        leg[resolvedField] = '';
      }
    };
    await geocodeEndpoint('startPoint', 'startResolved');
    await geocodeEndpoint('endPoint', 'endResolved');
    if (mySeq !== leg._seq) return;

    // Trasferimento: ARRIVO(N-1) -> PARTENZA(N). 0 se stesso punto (corsa a
    // catena che riparte da dove è arrivata la precedente) o prima corsa.
    let transitMin = 0;
    if (index > 0) {
      const prev = routes.value[index - 1];
      if (
        leg.startPoint &&
        prev.endPoint &&
        !samePoint(leg.startPoint, prev.endPoint)
      ) {
        try {
          const t = await fetchPair(prev.endPoint, leg.startPoint);
          transitMin = Math.round(t.min || 0);
        } catch (e) {
          transitMin = 0;
        }
      }
    }
    if (mySeq !== leg._seq) return; // superata da una richiesta più recente
    leg.transitMin = transitMin;

    if (!leg.startPoint || !leg.endPoint) {
      leg.previewKm = null;
      leg.previewMin = null;
      leg.previewError = '';
      leg.previewLoading = false;
      quoteLeg(leg);
      calculateArrivalTime(leg, index);
      return;
    }

    // Un estremo si può chiedere alle mappe se ha le coordinate (geolocalizzato)
    // oppure via E città. Con un testo parziale — quello che si sta ancora
    // scrivendo — il provider risponde «indirizzo non trovato»: meglio non
    // chiamarlo e dirlo qui.
    const usable = (p) =>
      !!p && ((Number(p.latitude) && Number(p.longitude)) || (String(p.address || '').trim() && String(p.city || '').trim()));
    if (!usable(leg.startPoint) || !usable(leg.endPoint)) {
      leg.previewKm = null;
      leg.previewMin = null;
      leg.previewError = 'indirizzo non riconosciuto: scrivi via, civico e città';
      leg.previewLoading = false;
      quoteLeg(leg);
      calculateArrivalTime(leg, index);
      return;
    }

    leg.previewLoading = true;
    leg.previewError = '';
    let pair = null;
    let errore = '';
    try {
      pair = await fetchPair(leg.startPoint, leg.endPoint);
    } catch (e) {
      pair = null;
      const dettaglio = e?.response?.data?.message;
      errore = Array.isArray(dettaglio) ? dettaglio.join(' · ') : dettaglio || 'distanza non calcolata';
    }
    if (mySeq !== leg._seq) return; // il vincitore aggiornerà lo stato
    leg.previewKm = typeof pair?.km === 'number' ? pair.km : null;
    leg.previewMin = typeof pair?.min === 'number' ? pair.min : null;
    // Corsa salvata con gli stessi estremi: vale la durata salvata.
    if (leg.previewMin !== null && leg._durataSalvata && estremiDi(leg) === leg._estremiSalvati) {
      leg.previewMin = leg._durataSalvata;
    }
    leg.previewError = errore;
    leg.previewLoading = false;
    quoteLeg(leg);
    calculateArrivalTime(leg, index);
  };

  // Ricalcola dalla corsa modificata in poi (le precedenti non dipendono da lei).
  // Il memo-cache rende gratuite le coppie invariate.
  const recomputeFrom = async (startIndex = 0) => {
    const from = Math.max(0, startIndex);
    for (let i = from; i < routes.value.length; i++) {
      // eslint-disable-next-line no-await-in-loop
      await applyPreview(routes.value[i], i);
    }
  };

  // ----- Blindatura reattività (watcher infallibili) --------------------------
  // Qualunque strada segua la modifica (autocomplete, @change, cascata, insert,
  // rechain), questi watcher garantiscono SEMPRE il ricalcolo a valle.
  //  - LUOGHI: firma sul testo digitato (stabile rispetto al geocoding, che
  //    sostituisce l'oggetto point ma non startText/endText) -> nessun loop.
  //  - ORARI: firma sui dateStart -> cascata sincrona idempotente (si arresta
  //    quando gli orari sono stabili: riscrivere lo stesso valore non ri-triggera).
  const placesSignature = computed(() =>
    routes.value
      .map((r) => `${normText(r.startText)}~${normText(r.endText)}`)
      .join('>>')
  );
  const timesSignature = computed(() =>
    routes.value.map((r) => r.dateStart || '').join('|')
  );
  const debouncedRecompute = debounce(() => recomputeFrom(0), 120);
  watch(placesSignature, () => debouncedRecompute());
  watch(timesSignature, () => {
    if (routes.value.length) calculateArrivalTime(routes.value[0], 0);
  });
  // La tariffa valida dipende dal giorno: cambiata la data, si riprezza tutto.
  watch(
    () => unref(date),
    () => routes.value.forEach((r) => quoteLeg(r))
  );
  // ...e dall'immatricolazione: la spunta «targa prova» passa la corsa al
  // listino dedicato. La casella è in v-model diretto sulla corsa, quindi si
  // osserva qui; il memo rende istantanee le corse che non sono cambiate.
  watch(
    () => routes.value.map((r) => [r.nonImmatricolato, ...(r.altreAuto || []).map((a) => a.nonImmatricolato)].map(Boolean).join(',')).join('|'),
    () => routes.value.forEach((r) => quoteLeg(r))
  );

  // ----- Catene: chi porta a casa ogni driver ---------------------------------------
  // La logica sta in `utils/catene.js` (la stessa del backend, che rifiuta il
  // salvataggio con le stesse regole): qui si traducono le corse dell'editor e
  // quelle già salvate degli altri spostamenti del giorno.
  const idDi = (leg) => `u${leg.uid}`;
  const minuti = (hhmm) => {
    if (!hhmm) return null;
    const [h, m] = String(hhmm).split(':').map(Number);
    return Number.isFinite(h) ? h * 60 + (m || 0) : null;
  };
  const hhmmDi = (iso) => {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  };
  const nomeLuogo = (testo) => String(testo || '').split(' — ')[0].trim();
  // Il nome di un punto salvato: la sede, se è una delle sedi proposte
  // (stesso indirizzo), altrimenti l'indirizzo.
  const nomePunto = (p) => {
    if (!p) return '';
    const k = chiaveLuogo(p);
    const sede = (headquartersCascader.value || []).find((o) => chiaveLuogo(o.point || o.value || o) === k);
    if (sede?.label) return nomeLuogo(sede.label);
    return [p.address, p.street_number].filter(Boolean).join(' ') || p.city || '';
  };

  // Corse GIÀ SALVATE di altri spostamenti dello stesso giorno: il driver può
  // rientrare a bordo di una di queste (es. un'auto aggiunta in un secondo
  // momento sullo stesso ritorno). Per ognuna: da dove parte la sua catena
  // e quanti posti sono già presi.
  const esterne = computed(() => {
    const mio = unref(journeyId);
    const giorni = (unref(giornata) || []).filter((j) => j && j.id !== mio);
    const tutte = giorni.flatMap((j) => (j.routes || []).filter((r) => r.status !== 'ELIMINATA'));
    const out = [];
    for (const j of giorni) {
      const vive = (j.routes || []).filter((r) => r.status !== 'ELIMINATA');
      const a = analizzaCatene(vive.map((r) => ({
        id: r.id,
        da: chiaveLuogo(r.startPoint),
        a: chiaveLuogo(r.endPoint),
        inizio: new Date(r.dateStart).getTime(),
        fine: new Date(r.dateEnd).getTime(),
        catena: r.two_operators === false,
        rientro: r.rientro_route_id || null,
        andata: r.andata_route_id || null,
      })));
      for (const r of vive) {
        if (r.two_operators !== false) continue;
        const radice = vive.find((x) => x.id === a.radice.get(r.id)) || r;
        out.push({
          id: `x${r.id}`,
          routeId: r.id,
          da: chiaveLuogo(r.startPoint),
          a: chiaveLuogo(r.endPoint),
          inizio: minuti(hhmmDi(r.dateStart)),
          fine: minuti(hhmmDi(r.dateEnd)),
          catena: true,
          esterna: true,
          origine: a.origine(r.id) ?? chiaveLuogo(radice.startPoint),
          postiPresi:
            tutte.filter((x) => x.rientro_route_id === r.id || x.andata_route_id === r.id).length +
            (r.passengers || []).filter((p) => !p.from_route_id && !p.to_route_id).length,
          ora: hhmmDi(r.dateStart),
          percorso: `${nomePunto(r.startPoint)} → ${nomePunto(r.endPoint)}`,
          auto: [r.marca, r.modello, r.targa].filter(Boolean).join(' '),
        });
      }
    }
    return out;
  });

  // Il rientro come lo vede l'analisi. Una scelta ancora da fare è '?': la
  // corsa non prosegue la catena di nessuno (e `validate` la ferma).
  const rientroDi = (leg) => {
    if (leg.isShuttle !== false || !leg.rientroAltrove) return null;
    return leg.rientroSu || '?';
  };
  const corseBase = computed(() => [
    ...routes.value.map((leg) => ({
      id: idDi(leg),
      da: chiaveLuogo(leg.startPoint),
      a: chiaveLuogo(leg.endPoint),
      inizio: minuti(leg.dateStart),
      fine: minuti(leg.dateEnd),
      catena: leg.isShuttle === false,
      padre: leg.autoChained && leg.chainedFrom ? `u${leg.chainedFrom}` : null,
      rientro: rientroDi(leg),
    })),
    ...esterne.value,
  ]);
  // Le auto in più, come corse a sé: a catena il loro driver rientra a bordo
  // della corsa con cui torna il driver della prima auto (quella che prosegue
  // la sua catena, o quella su cui rientra lui). '?' = ancora da decidere.
  const analisiBase = computed(() => analizzaCatene(corseBase.value));
  const ritornoDelleAuto = (leg) => {
    if (!leg || leg.isShuttle !== false) return null;
    const figlio = analisiBase.value.figlio.get(idDi(leg));
    if (figlio) return figlio;
    return leg.rientroAltrove && leg.rientroSu ? leg.rientroSu : null;
  };
  // La corsa dell'elenco che prosegue / precede `leg` nella catena del suo
  // driver (contando solo le prime auto).
  const legDaId = (id) => (id ? routes.value.find((r) => idDi(r) === id) || null : null);
  const seguitoDi = (leg) => legDaId(analisiBase.value.figlio.get(idDi(leg)));
  const precedenteDi = (leg) => legDaId(analisiBase.value.padre.get(idDi(leg)));
  // PIÙ AUTO ANCHE AL RITORNO: 2 auto A → B e 2 auto B → A, ognuno riporta
  // un'auto. Il driver dell'auto k (oltre la prima) di una corsa a catena
  // guida l'auto k della corsa che prosegue la catena, se c'è; altrimenti
  // rientra a bordo della corsa con cui torna il driver della prima — o la
  // sua catena si chiude lì.
  const chiudeBase = (leg) => {
    const radice = legDaId(analisiBase.value.radice.get(idDi(leg))) || leg;
    return radice !== leg && !!leg.endPoint && chiaveLuogo(leg.endPoint) === chiaveLuogo(radice.startPoint);
  };
  const rientroAuto = (leg, k) => {
    if (!leg || leg.isShuttle !== false) return null;
    const seguito = seguitoDi(leg);
    if (seguito && seguito.isShuttle === false && k < (seguito.altreAuto || []).length) return null;
    if (chiudeBase(leg)) return null;
    return ritornoDelleAuto(leg) || '?';
  };
  // L'auto k di una corsa che prosegue una catena: la guida il driver
  // dell'auto k della corsa da cui riparte.
  const padreAuto = (leg, k) => {
    const prima = precedenteDi(leg);
    if (!prima || prima.isShuttle !== false || leg.isShuttle !== false) return null;
    const a = (prima.altreAuto || [])[k];
    return a ? `${idDi(prima)}-a${a.uid}` : null;
  };
  // ANDATA A BORDO, il contrario: 1 auto A → B e 2 auto B → A. Le auto in
  // più rispetto alla corsa da cui riparte le guidano driver arrivati A BORDO
  // di quella corsa (uno guida, gli altri sono passeggeri): la loro catena
  // parte da dove parte quella, e tornandoci si chiude.
  const andataAuto = (leg, k) => {
    if (!leg || leg.isShuttle !== false) return null;
    const prima = precedenteDi(leg);
    if (!prima || prima.isShuttle !== false) return null;
    if (k < (prima.altreAuto || []).length) return null;
    return idDi(prima);
  };
  const autoInPiu = computed(() =>
    routes.value.flatMap((leg) =>
      (leg.altreAuto || []).map((auto, k) => ({
        id: `${idDi(leg)}-a${auto.uid}`,
        da: chiaveLuogo(leg.startPoint),
        a: chiaveLuogo(leg.endPoint),
        inizio: minuti(leg.dateStart),
        fine: minuti(leg.dateEnd),
        catena: leg.isShuttle === false,
        padre: padreAuto(leg, k),
        rientro: rientroAuto(leg, k),
        andata: andataAuto(leg, k),
        leg,
        auto,
        k,
      }))
    )
  );
  const corseCatena = computed(() => [...corseBase.value, ...autoInPiu.value]);
  const analisi = computed(() => analizzaCatene(corseCatena.value));
  const statoDi = (leg) => analisi.value.stato.get(idDi(leg));

  /** La prima corsa della catena di `leg` (da dove è partito il driver). */
  const radiceDi = (leg) => {
    const id = analisi.value.radice.get(idDi(leg));
    return routes.value.find((r) => idDi(r) === id) || leg;
  };

  /**
   * La catena si CHIUDE: la corsa riporta il driver da dove è partita la SUA
   * catena (non per forza la prima corsa della giornata: con più auto e più
   * giri, ognuno ha il suo punto di partenza).
   */
  const chiudeLaCatena = (leg) => !!leg && leg.isShuttle === false && statoDi(leg) === 'chiude';

  // ----- Rientro a bordo ----------------------------------------------------------
  // Il driver di una corsa a catena può rientrare come passeggero su un'altra
  // corsa a catena che parte da dove arriva lui, dopo il suo arrivo, e lo
  // riporta da dove era partito; al massimo 4 per auto. Anche di un altro
  // spostamento dello stesso giorno.
  // Il numero di corsa di un id (anche di un'auto in più: quello della corsa).
  const numeroDi = (id) => routes.value.findIndex((r) => idDi(r) === String(id).split('-a')[0]) + 1;
  const corsaDiRientro = (leg) => {
    if (!leg?.rientroAltrove || leg.isShuttle !== false || !leg.rientroSu) return null;
    return statoDi(leg) === 'a_bordo' ? leg.rientroSu : null;
  };
  // Il driver rientra altrove ma non si è ancora detto con quale corsa (o
  // quella scelta non va più bene).
  const rientroDaScegliere = (leg) =>
    !!leg && leg.isShuttle === false && !!leg.rientroAltrove && !chiudeLaCatena(leg) &&
    !corsaDiRientro(leg);
  const opzioniRientro = (index) => {
    const leg = routes.value[index];
    if (!leg) return [];
    const ipotesi = corseCatena.value.map((c) => (c.id === idDi(leg) ? { ...c, rientro: null } : c));
    return opzioniCatena(ipotesi, idDi(leg)).filter(({ id }) => !id.includes('-a')).map(({ id, liberi }) => {
      const posti = `${liberi} ${liberi === 1 ? 'posto libero' : 'posti liberi'}`;
      const est = esterne.value.find((e) => e.id === id);
      if (est) {
        return {
          value: id,
          titolo: `corsa delle ${est.ora} · ${est.percorso}`,
          dettaglio: [`altro spostamento`, est.auto, posti].filter(Boolean).join(' · '),
        };
      }
      const r = routes.value.find((x) => idDi(x) === id);
      // Un singolo: si rientra col suo jolly, che va a prendere il driver
      // dove arriva la corsa e lo riporta alla base.
      if (r.isShuttle !== false) {
        return {
          value: id,
          jolly: true,
          titolo: `Corsa ${numeroDi(id)} · ${r.dateStart || '--:--'} · ${nomeLuogo(r.startText)} → ${nomeLuogo(r.endText) || '…'}`,
          dettaglio: [`il jolly lo riprende a ${nomeLuogo(r.endText) || 'destinazione'} e lo riporta a ${nomeLuogo(radiceDi(r).startText) || 'la base'}`, posti].join(' · '),
        };
      }
      return {
        value: id,
        titolo: `Corsa ${numeroDi(id)} · ${r.dateStart || '--:--'} · ${nomeLuogo(r.startText)} → ${nomeLuogo(r.endText) || '…'}`,
        dettaglio: [[r.marca, r.modello, r.targa].filter(Boolean).join(' '), posti].filter(Boolean).join(' · '),
      };
    });
  };
  const scegliRientro = (index, value) => {
    const leg = routes.value[index];
    if (leg) leg.rientroSu = value || null;
  };
  // «Scegli come rientra»: il driver non torna con una corsa sua.
  const scegliComeRientra = (index) => {
    const leg = routes.value[index];
    if (!leg) return;
    leg.rientroAltrove = true;
    leg.rientroSu = null;
    // Il rientro proposto in automatico (ancora intonso) non serve più.
    sincronizzaCatena(index);
    rechain();
    recomputeFrom(index);
  };
  // Al posto del rientro proposto in automatico il driver può tornare con
  // una corsa già prevista: a bordo di un'altra catena o col jolly di un
  // singolo. Il rientro proposto non conta come alternativa.
  const puoRientrareAltrove = (index) => {
    const leg = routes.value[index];
    const next = routes.value[index + 1];
    if (!leg || leg.isShuttle !== false || leg.rientroAltrove) return false;
    if (!(next && next.autoChained && next.chainedFrom === leg.uid && autoChildIntonsa(next))) return false;
    return opzioniRientro(index).some((o) => o.value !== idDi(next));
  };

  // ----- Auto in più sulla stessa corsa ---------------------------------------------
  const aggiungiAuto = (index) => {
    const leg = routes.value[index];
    if (!leg || 1 + leg.altreAuto.length >= MAX_AUTO) return;
    leg.altreAuto.push(makeAuto());
    quoteLeg(leg);
  };
  // Ritorna l'id della corsa salvata da eliminare (edit mode), se c'era.
  const togliAuto = (index, k) => {
    const leg = routes.value[index];
    if (!leg || !leg.altreAuto[k]) return null;
    const [tolta] = leg.altreAuto.splice(k, 1);
    quoteLeg(leg);
    return tolta.sourceId || null;
  };
  // Le auto si aggiungono dove un driver PARTE: la prima corsa della sua
  // catena, o una singola. Un ritorno (o una corsa che prosegue una catena)
  // ha una vettura sola: chi c'è in più rientra a bordo.
  const puoAggiungereAuto = (index) => {
    const leg = routes.value[index];
    if (!leg || 1 + leg.altreAuto.length >= MAX_AUTO) return false;
    const id = idDi(leg);
    // Dove i driver PARTONO: ognuno con la sua auto.
    if (!leg.autoChained && analisi.value.radice.get(id) === id && !(analisi.value.passeggeri.get(id) || []).length) {
      return true;
    }
    // Il seguito a catena di una catena (es. il ritorno): le auto le guidano
    // i driver arrivati con la corsa da cui riparte; quelle in più, driver
    // arrivati A BORDO di quella corsa (andata a bordo), finché ci sono posti.
    const prima = precedenteDi(leg);
    if (!prima || leg.isShuttle !== false || prima.isShuttle !== false) return false;
    const idPrima = idDi(prima);
    const aBordo = (analisi.value.andate.get(idPrima) || []).length
      + (analisi.value.passeggeri.get(idPrima) || []).length;
    return leg.altreAuto.length < (prima.altreAuto || []).length || aBordo < MAX_PASSEGGERI;
  };
  const autoCompleta = (auto) => !!(auto.marca && auto.modello && (auto.targa || auto.nonImmatricolato));
  // Cosa succede con più auto, in parole.
  const notaAuto = (index) => {
    const leg = routes.value[index];
    const n = 1 + (leg?.altreAuto?.length || 0);
    if (!leg || n < 2) return '';
    if (leg.isShuttle !== false) {
      return `${n} auto partono insieme: ognuna ha il suo driver e il suo jolly.`;
    }
    // Il seguito della catena con più auto: le guidano i driver arrivati.
    const prima = precedenteDi(leg);
    if (prima && prima.isShuttle === false) {
      const arrivate = 1 + (prima.altreAuto || []).length;
      const N = numeroDi(idDi(prima));
      if (arrivate < n) {
        const aBordo = n - arrivate;
        return `${n} auto: ${arrivate === 1 ? 'una la guida il driver arrivato' : `${arrivate} le guidano i driver arrivati`} ` +
          `con la corsa ${N}, ${aBordo === 1 ? "l'altra la guida un driver arrivato" : `le altre ${aBordo} le guidano driver arrivati`} ` +
          `a bordo della stessa corsa.`;
      }
      return `${n} auto: le guidano i driver arrivati con la corsa ${N}.`;
    }
    // Più auto anche nella corsa che prosegue: ognuno ne guida una.
    const dopo = seguitoDi(leg);
    const guidano = dopo && dopo.isShuttle === false ? Math.min(n - 1, (dopo.altreAuto || []).length) : 0;
    if (guidano) {
      const restano = n - 1 - guidano;
      const N = numeroDi(idDi(dopo));
      return `${n} auto partono insieme, ognuna con il suo driver. ` + (restano
        ? `Nella corsa ${N} ${guidano === 1 ? 'un altro driver guida' : `altri ${guidano} driver guidano`} le altre auto, `
          + `${restano === 1 ? "l'ultimo rientra" : `gli ultimi ${restano} rientrano`} a bordo.`
        : `Nella corsa ${N} ognuno guida un'auto.`);
    }
    const ritorno = ritornoDelleAuto(leg);
    if (!ritorno) {
      return `${n} auto partono insieme, ognuna con il suo driver: indica con quale corsa rientra il driver della prima, ` +
        'e gli altri rientrano a bordo con lui.';
    }
    const est = esterne.value.find((e) => e.id === ritorno);
    // Il seguito della catena è uno spostamento singolo: lì a bordo non
    // rientra nessuno, gli altri driver resterebbero a piedi.
    const seguito = routes.value.find((r) => idDi(r) === ritorno);
    if (seguito && seguito.isShuttle !== false) {
      return `${n} auto partono insieme, ognuna con il suo driver. La corsa ${numeroDi(ritorno)} è uno spostamento ` +
        `singolo: a bordo non rientra nessuno, quindi ${n - 1 === 1 ? "l'altro driver resterebbe" : `gli altri ${n - 1} driver resterebbero`} ` +
        `senza rientro. Serve una corsa a catena che ${n - 1 === 1 ? 'lo' : 'li'} riporti.`;
    }
    const su = est ? `della corsa delle ${est.ora} di un altro spostamento` : `della corsa ${numeroDi(ritorno)}`;
    return `${n} auto partono insieme, ognuna con il suo driver. Al ritorno basta un'auto: ` +
      `${n - 1 === 1 ? "l'altro driver rientra" : `gli altri ${n - 1} driver rientrano`} a bordo ${su}.`;
  };

  // ----- Chaining ---------------------------------------------------------------
  // Sincronizza SOLO le corse auto-generate: partenza = destinazione del padre.
  const rechain = () => {
    for (let i = 0; i < routes.value.length; i++) {
      const leg = routes.value[i];
      if (leg.autoChained) {
        const parent =
          routes.value.find((r) => r.uid === leg.chainedFrom) ||
          routes.value[i - 1];
        if (parent) {
          leg.startPoint = parent.endPoint;
          leg.startText = parent.endText;
          leg._lastStartCommit = leg.startText || '';
          // Il rientro riporta il driver da dove è partita la SUA catena
          // (A → B → C, poi C → A): l'arrivo la segue finché non lo cambia
          // chi compila.
          if (leg._endAuto) {
            const origine = radiceDi(parent);
            leg.endPoint = origine.startPoint ? { ...origine.startPoint } : null;
            leg.endText = origine.startText || '';
            leg._lastEndCommit = leg.endText;
          }
        }
      }
    }
  };

  const setShuttle = (index, value) => {
    const leg = routes.value[index];
    if (!leg || leg.isShuttle === value) return;
    leg.isShuttle = value;
    onShuttleChange(index);
    // Il numero di operatori cambia la fascia, quindi il costo.
    quoteLeg(leg);
  };

  // Una corsa che prosegue una catena e riporta il driver da dove è partita
  // la catena la chiude: è a catena, non singola (il jolly non serve). Il
  // singolo lì non si sceglie, e se lo era (arrivo cambiato) torna a catena.
  const tornaAllaBase = (leg) => {
    if (!leg?.endPoint || !analisi.value.padre.get(idDi(leg))) return false;
    const origine = radiceDi(leg);
    return origine !== leg && !!origine.startPoint && chiaveLuogo(leg.endPoint) === chiaveLuogo(origine.startPoint);
  };
  const soloCatena = (index) => {
    const leg = routes.value[index];
    if (!tornaAllaBase(leg)) return '';
    const base = nomeLuogo(radiceDi(leg).startText) || 'la partenza della catena';
    return `Riporta il driver a ${base}, da dove è partita la catena: è uno spostamento a catena.`;
  };
  watch(
    () => routes.value.map((leg, i) => (leg.isShuttle !== false && tornaAllaBase(leg) ? i : -1)).filter((i) => i >= 0),
    (singoli) => singoli.forEach((i) => setShuttle(i, false)),
  );

  // Una corsa auto-generata e ancora INTONSA si può togliere senza rimpianti;
  // se l'operatore ha già scritto qualcosa, o se è una tratta già salvata,
  // resta dov'è e la toglie lui.
  const autoChildIntonsa = (leg) =>
    !!leg &&
    leg.autoChained &&
    !leg.sourceId &&
    (!leg.endPoint || leg._endAuto) &&
    !leg.marca &&
    !leg.modello &&
    !leg.targa;

  /**
   * Allinea la coppia "corsa a catena + rientro" allo stato della corsa.
   *
   * La si chiama sia quando cambia il tipo di spostamento sia quando cambia la
   * destinazione: a catena il rientro serve, a meno che la catena non si
   * chiuda, che un'altra corsa la prosegua già o che il driver rientri a
   * bordo di un'altra corsa; singolo non serve mai.
   */
  const sincronizzaCatena = (index) => {
    const leg = routes.value[index];
    if (!leg) return;
    const nextIdx = index + 1;
    const next = routes.value[nextIdx];
    const hasAutoChild = next && next.autoChained && next.chainedFrom === leg.uid;
    const figlio = analisi.value.figlio.get(idDi(leg));
    const proseguitaDaAltra = !!figlio && !(hasAutoChild && figlio === idDi(next));
    const serveRientro = leg.isShuttle === false && !chiudeLaCatena(leg) && !leg.rientroAltrove && !proseguitaDaAltra;

    if (serveRientro) {
      if (!hasAutoChild) {
        const origine = radiceDi(leg);
        const child = makeLeg({
          autoChained: true,
          chainedFrom: leg.uid,
          startPoint: leg.endPoint,
          startText: leg.endText,
          dateStart: leg.dateEnd || '',
          // Il RIENTRO: riporta il driver da dove è partita la sua catena
          // (Sede A -> Sede B, poi Sede B -> Sede A; e dopo B -> C, C -> A).
          // L'arrivo resta modificabile; finché nessuno lo tocca segue
          // quella partenza.
          endPoint: origine.startPoint ? { ...origine.startPoint } : null,
          endText: origine.startText || '',
          _endAuto: true,
          // Il driver è rimasto a destinazione da solo: anche la corsa che
          // riparte da lì la fa da solo, quindi nasce «a catena».
          isShuttle: false,
        });
        child._lastEndCommit = child.endText;
        routes.value.splice(nextIdx, 0, child);
      }
    } else if (hasAutoChild && (leg.isShuttle === true || autoChildIntonsa(next))) {
      routes.value.splice(nextIdx, 1);
    }
  };

  // Spiegazione di una corsa a catena, con i nomi delle sedi: cosa succede al
  // driver. Il caso normale (il driver porta l'auto e da lì parte la corsa
  // successiva) sta in una «i» di informazione; si scrive per esteso solo
  // quello che chi compila deve sapere o fare.
  // { testo, info, tipo }: tipo 'ok' | 'info' | 'scelta' | 'scoperto'.
  const notaCorsa = (index) => {
    const leg = routes.value[index];
    if (!leg) return null;
    const a = analisi.value;
    const id = idDi(leg);
    // Singolo che riparte da dove una catena ha lasciato il driver: lo guida
    // lui, e il jolly lo raggiunge e lo riporta alla partenza della catena.
    // Lo organizza Simba: chi compila lo legge nella «i».
    if (leg.isShuttle !== false) {
      if (!a.padre.get(id) || !leg.endPoint) return null;
      const base = nomeLuogo(radiceDi(leg).startText) || 'la partenza della catena';
      const conLui = [...new Set((a.passeggeri.get(id) || []).map(numeroDi).filter(Boolean))];
      const anche = conLui.length
        ? ` Riporta anche ${conLui.length === 1 ? `il driver della corsa ${conLui[0]}` : `i driver delle corse ${conLui.join(', ')}`}.`
        : '';
      return { tipo: 'ok', testo: '', info: `Il jolly raggiunge il driver a ${nomeLuogo(leg.endText)} e lo riporta a ${base}.${anche}` };
    }
    const origine = nomeLuogo(radiceDi(leg).startText) || 'il punto di partenza';
    const arrivo = nomeLuogo(leg.endText);
    // Chi rientra a bordo, per corsa: «il driver della corsa 3», «2 driver
    // della corsa 1» (le auto in più di una corsa hanno lo stesso numero).
    const perCorsa = new Map();
    (a.passeggeri.get(id) || []).map(numeroDi).filter(Boolean)
      .forEach((n) => perCorsa.set(n, (perCorsa.get(n) || 0) + 1));
    const parti = [...perCorsa].map(([n, quanti]) =>
      (quanti === 1 ? `il driver della corsa ${n}` : `${quanti} driver della corsa ${n}`));
    const totale = [...perCorsa.values()].reduce((t, q) => t + q, 0);
    const aBordo = parti.length
      ? `A bordo rientra${totale > 1 ? 'no' : ''} anche ` +
        `${parti.length > 1 ? `${parti.slice(0, -1).join(', ')} e ${parti[parti.length - 1]}` : parti[0]}.`
      : '';
    const info = arrivo
      ? `A catena: il driver porta l'auto a ${arrivo} e da lì parte la corsa successiva; ` +
        `la catena si chiude quando una corsa lo riporta a ${origine}.`
      : `A catena: indica dove il driver porta l'auto. Se lo riporta a ${origine} la catena è chiusa, ` +
        'altrimenti da lì parte la corsa successiva.';
    if (!leg.endPoint) return { tipo: 'info', testo: '', info };
    if (rientroDaScegliere(leg)) {
      const motivo = leg.rientroSu ? a.rientriNonValidi.get(id) : null;
      return {
        tipo: 'scelta',
        testo: motivo
          ? `Il rientro scelto non va più bene: ${motivo}. Scegli di nuovo come rientra il driver da ${arrivo}.`
          : `Il driver resta a ${arrivo}: con quale corsa rientra?`,
        info,
      };
    }
    const stato = a.stato.get(id);
    // Tutto a posto: la spiegazione sta nella «i», in vista resta solo quello
    // che chiede di fare qualcosa (scegliere il rientro, driver scoperto).
    if (stato === 'chiude') {
      return { tipo: 'ok', testo: '', info: `A catena: questa corsa riporta il driver a ${origine}, la catena è chiusa. ${aBordo}`.trim() };
    }
    if (stato === 'a_bordo') {
      const est = esterne.value.find((e) => e.id === leg.rientroSu);
      // Col jolly di un singolo che prosegue una catena.
      const singolo = !est && routes.value.find((r) => idDi(r) === leg.rientroSu && r.isShuttle !== false);
      if (singolo) {
        return {
          tipo: 'ok',
          testo: '',
          info: `A catena: il driver porta l'auto a ${arrivo} e rientra con il jolly della corsa ${numeroDi(leg.rientroSu)}, `
            + `che lo riporta a ${origine}.`,
        };
      }
      const su = est
        ? `della corsa delle ${est.ora} (${est.percorso}) di un altro spostamento`
        : `della corsa ${numeroDi(leg.rientroSu)}` +
          `${routes.value.find((r) => idDi(r) === leg.rientroSu)?.dateStart ? ` (partenza ${routes.value.find((r) => idDi(r) === leg.rientroSu).dateStart})` : ''}`;
      return { tipo: 'ok', testo: '', info: `A catena: il driver porta l'auto a ${arrivo} e rientra a bordo ${su}, come passeggero.` };
    }
    if (stato === 'scoperto') {
      return { tipo: 'scoperto', testo: `Il driver resta a ${arrivo} senza rientro.`, info };
    }
    // prosegue: il caso normale, solo la «i» (con chi rientra a bordo)
    return { tipo: 'info', testo: '', info: `${info} ${aBordo}`.trim() };
  };

  // Spostamento già salvato: il rientro a bordo punta a una corsa (di questo
  // spostamento o di un altro). Una
  // corsa a catena che resta scoperta chiede come rientra il driver.
  routes.value.forEach((leg) => {
    if (leg.isShuttle !== false) return;
    if (leg.rientroSu) leg.rientroAltrove = true;
  });
  routes.value.forEach((leg) => {
    if (leg.isShuttle !== false || leg.rientroAltrove || !leg.sourceId) return;
    if (statoDi(leg) === 'scoperto') {
      leg.rientroAltrove = true;
      leg.rientroSu = null;
    }
  });

  // Ci si è ripensati: il rientro torna, proposto come prima.
  const aggiungiRientro = (index) => {
    const leg = routes.value[index];
    if (!leg) return;
    leg.rientroAltrove = false;
    leg.rientroSu = null;
    sincronizzaCatena(index);
    rechain();
    recomputeFrom(index);
  };

  // "Spostamento a catena": auto-append corsa dalla DESTINAZIONE (partenza bloccata).
  // "Spostamento a 2 operatori": rimuove l'eventuale corsa auto-generata.
  const onShuttleChange = (index) => {
    if (!routes.value[index]) return;
    sincronizzaCatena(index);
    rechain();
    recomputeFrom(index);
  };

  // Punto di partenza di default per una corsa che segue `prev`:
  //  - "Spostamento a 2 operatori": il Jolly ha RIPORTATO il driver alla base
  //    -> si eredita il Punto di PARTENZA della corsa precedente.
  //  - "Spostamento a catena": il driver è rimasto a destinazione -> si eredita
  //    il Punto di ARRIVO. In entrambi i casi il campo resta editabile.
  const seedStartFrom = (prev) => {
    if (!prev) return { startPoint: null, startText: '' };
    if (prev.isShuttle) {
      return {
        startPoint: prev.startPoint ? { ...prev.startPoint } : null,
        startText: prev.startText || '',
      };
    }
    return {
      startPoint: prev.endPoint ? { ...prev.endPoint } : null,
      startText: prev.endText || '',
    };
  };

  // Dopo una corsa a catena rimasta aperta, la corsa aggiunta la prosegue:
  // a catena, verso il punto da cui è partita la catena (A → B → C, poi C → A).
  const proseguiCatena = (prev) => {
    if (!prev || prev.isShuttle !== false || prev.rientroAltrove || statoDi(prev) !== 'scoperto') return {};
    const origine = radiceDi(prev);
    return {
      isShuttle: false,
      endPoint: origine.startPoint ? { ...origine.startPoint } : null,
      endText: origine.startText || '',
    };
  };

  // "+ Aggiungi Corsa" (in fondo alla timeline).
  const addRouteAtEnd = () => {
    const last = routes.value[routes.value.length - 1];
    const leg = makeLeg({ ...seedStartFrom(last), ...proseguiCatena(last), autoChained: false });
    routes.value.push(leg);
    rechain();
    recomputeFrom(routes.value.length - 1);
  };

  // Inserimento IN-LINE: nuova corsa SUBITO DOPO la corsa `index` (cioè PRIMA
  // della corsa successiva già esistente).
  //  - Regola di partenza: l'orario proposto è limitato in alto all'orario
  //    della corsa successiva meno 1 minuto.
  //  - Sforamento: se completando la corsa gli orari delle successive vengono
  //    spinti in avanti, chiediamo conferma (confirmOverlapShift). Su conferma
  //    lo shift a cascata resta applicato; su rifiuto si annulla l'inserimento
  //    ripristinando gli orari originali.
  const insertRouteAfter = async (index) => {
    const prev = routes.value[index];
    if (!prev) return;
    const nextExisting = routes.value[index + 1] || null;
    const leg = makeLeg({ ...seedStartFrom(prev), autoChained: false });

    if (nextExisting && nextExisting.dateStart) {
      // Proposta = floor (arrivo precedente) limitato a [successiva - 1 min].
      const cap = addMinutesToTime(nextExisting.dateStart, -1);
      const floor = prev.dateEnd || prev.dateStart || '';
      let proposed = floor;
      if (!proposed || (cap && proposed > cap)) proposed = cap;
      leg.dateStart = proposed;
      // Guardia: proteggi l'orario originale della corsa successiva.
      leg._guardNextUid = nextExisting.uid;
      leg._guardNextStart = nextExisting.dateStart;
    }

    routes.value.splice(index + 1, 0, leg);
    rechain();
    await recomputeFrom(index + 1);
    await resolveInsertGuard(leg);
  };

  // Verifica lo sforamento di una corsa inserita: se la corsa successiva
  // protetta è stata spinta oltre il suo orario originale, chiede conferma.
  //  - Conferma: lo shift a cascata (già applicato) resta.
  //  - Rifiuto: rimuove la corsa inserita; le successive tornano al floor
  //    naturale (ricalcolo senza la tratta inserita), quindi senza overlap.
  const resolveInsertGuard = async (leg) => {
    if (!leg || !leg._guardNextUid) return;
    const nextIdx = indexOfUid(leg._guardNextUid);
    if (nextIdx === -1) {
      leg._guardNextUid = null;
      return;
    }
    const nextLeg = routes.value[nextIdx];
    const shifted =
      nextLeg.dateStart &&
      leg._guardNextStart &&
      nextLeg.dateStart > leg._guardNextStart;
    if (!shifted) return; // nessun overlap: nessuna conferma richiesta

    // Cattura l'uid della corsa in conflitto PRIMA di azzerare la guardia
    // (evita richieste concorrenti sullo stesso inserimento).
    const nextUid = leg._guardNextUid;
    leg._guardNextUid = null;
    leg._guardNextStart = null;

    const choice = await resolveOverlap();

    // 'shift' (Procedi): lo slittamento a cascata è già applicato -> nulla da fare.
    if (choice === 'shift') return;

    debouncedRecompute.cancel();

    // 'after' (Inserisci dopo): la corsa inserita si sposta SUBITO DOPO quella
    // in conflitto. Quest'ultima torna al suo orario originale (niente shift);
    // la nuova corsa riparte dalla fine della corsa in conflitto.
    if (choice === 'after') {
      // Estrai la corsa inserita e reinseriscila DOPO quella in conflitto.
      const li = indexOfUid(leg.uid);
      if (li !== -1) routes.value.splice(li, 1);
      const ni = indexOfUid(nextUid);
      const insertAt = ni === -1 ? routes.value.length : ni + 1;
      routes.value.splice(insertAt, 0, leg);
      // Riordino strutturale: azzero transiti e orari dalla corsa in conflitto
      // in poi, così nessun ricalcolo intermedio usa un transito stantio.
      // La corsa in conflitto torna al suo floor (>= non è mai spinta in avanti).
      const from = ni === -1 ? 1 : ni;
      for (let k = from; k < routes.value.length; k++) {
        routes.value[k].transitMin = 0;
        if (k > 0) routes.value[k].dateStart = '';
      }
      rechain();
      await recomputeFrom(Math.max(0, from - 1));
      return;
    }

    // 'cancel' (Annulla): rimuove la corsa inserita e ripropone gli orari a
    // valle dal floor naturale (nessuna modifica permanente all'array).
    const i = indexOfUid(leg.uid);
    if (i !== -1) routes.value.splice(i, 1);
    for (let k = Math.max(1, i); k < routes.value.length; k++) {
      routes.value[k].dateStart = '';
    }
    rechain();
    await recomputeFrom(0);
  };

  // L'inserimento non è consentito tra uno "Spostamento a catena" e la corsa
  // che lo segue (coppia indivisibile, anche in edit mode dove il legame
  // autoChained non è ricostruito); e non serve dopo l'ultima (c'è il
  // bottone principale).
  const canInsertAfter = (index) => {
    if (index >= routes.value.length - 1) return false;
    const cur = routes.value[index];
    // Coppia indivisibile solo finché c'è un rientro da fare: se la corsa
    // chiude la catena non ha una seconda metà, e in mezzo ci si può infilare.
    // Con le corse in contemporanea la corsa dopo può essere un'altra auto:
    // la coppia c'è solo se riparte da dove questa arriva.
    const next = routes.value[index + 1];
    if (cur && cur.isShuttle === false && !chiudeLaCatena(cur) && !cur.rientroAltrove &&
        samePoint(next?.startPoint, cur.endPoint)) return false;
    return !(next && next.autoChained && next.chainedFrom === cur.uid);
  };

  // Rimuove la corsa (e l'eventuale figlia auto-generata). Ritorna gli id delle
  // Route PERSISTITE rimosse (sourceId), per il soft-delete a cura del parent.
  const removeRoute = (index) => {
    if (routes.value.length <= 1) return [];
    const leg = routes.value[index];
    if (!leg) return [];
    const removedSourceIds = [];
    if (leg.sourceId) removedSourceIds.push(leg.sourceId);
    (leg.altreAuto || []).forEach((a) => a.sourceId && removedSourceIds.push(a.sourceId));
    const next = routes.value[index + 1];
    if (next && next.autoChained && next.chainedFrom === leg.uid) {
      if (next.sourceId) removedSourceIds.push(next.sourceId);
      routes.value.splice(index + 1, 1);
    }
    routes.value.splice(index, 1);
    rechain();
    recomputeFrom(Math.max(0, index - 1));
    return removedSourceIds;
  };

  // La corsa che SEGUE uno "Spostamento a catena" non è eliminabile da sola
  // (è la seconda metà della catena): si elimina eliminando la corsa madre.
  // A meno che la corsa madre non chiuda già la catena: allora quella dopo è
  // una corsa a sé, e si toglie come tutte le altre.
  const canRemove = (index) => {
    if (routes.value.length <= 1) return false;
    const prev = index > 0 ? routes.value[index - 1] : null;
    if (prev?.isShuttle === false && !chiudeLaCatena(prev) && !prev.rientroAltrove &&
        samePoint(routes.value[index]?.startPoint, prev.endPoint)) return false;
    return true;
  };

  // ----- Commit punti (anti doppio-fuoco + debounce per campo) ------------------
  const commitStart = async (index, val) => {
    const leg = routes.value[index];
    if (!leg) return;
    const v = (val || '').trim();
    if (v === leg._lastStartCommit) return; // @select seguito da @change identico
    leg._lastStartCommit = v;
    leg.startPoint = resolvePoint(v);
    leg.startText = v;
    leg.startResolved = ''; // etichetta stantia: il geocoding la rigenera
    // Sede cambiata -> l'orario va ricalcolato dal nuovo trasferimento (il
    // floor viene riproposto in automatico dalla cascata). Prima corsa esclusa.
    if (index > 0) leg.dateStart = '';
    rechain();
    await recomputeFrom(index);
    if (leg._guardNextUid) await resolveInsertGuard(leg);
  };
  const commitEnd = async (index, val) => {
    const leg = routes.value[index];
    if (!leg) return;
    const v = (val || '').trim();
    if (v === leg._lastEndCommit) return;
    leg._lastEndCommit = v;
    leg.endPoint = resolvePoint(v);
    leg.endText = v;
    leg.endResolved = '';
    // Scritto a mano: non segue più la partenza della corsa precedente.
    leg._endAuto = false;
    // Il seguito di una catena mandato altrove (non alla partenza della
    // catena) diventa SINGOLO: lo guida il driver arrivato lì e il jolly lo
    // riporta (A → B a catena, B → C singolo). Niente corsa di rientro in più;
    // chi vuole proprio la catena la rimette col pulsante.
    if (leg.isShuttle === false && leg.endPoint && analisi.value.padre.get(idDi(leg))) {
      const origine = radiceDi(leg);
      if (origine !== leg && origine.startPoint && chiaveLuogo(leg.endPoint) !== chiaveLuogo(origine.startPoint)) {
        leg.isShuttle = true;
      }
    }
    // La destinazione decide se la catena si chiude: scritta DOPO aver scelto
    // «1 operatore», deve poter togliere il rientro che era stato proposto.
    sincronizzaCatena(index);
    rechain();
    await recomputeFrom(index);
    if (leg._guardNextUid) await resolveInsertGuard(leg);
  };

  // Debouncer per corsa+campo (mai condiviso: nessun commit può andare perso).
  const debouncers = new Map();
  const debounced = (key, fn) => {
    if (!debouncers.has(key)) {
      debouncers.set(key, debounce((f) => f(), 350));
    }
    debouncers.get(key)(fn);
  };
  const cancelDebounce = (key) => {
    const d = debouncers.get(key);
    if (d) d.cancel();
  };

  // Risolve l'indice ATTUALE della corsa dal suo uid (gli indici possono
  // slittare se una corsa viene rimossa mentre un commit è in debounce).
  const indexOfUid = (uid) => routes.value.findIndex((r) => r.uid === uid);

  // @select: immediato (e annulla il debounce pendente). @change: debounced.
  const onStartSelect = (val, index) => {
    const uid = routes.value[index]?.uid;
    cancelDebounce(`s${uid}`);
    commitStart(index, val);
  };
  const onStartChange = (val, index) => {
    const uid = routes.value[index]?.uid;
    debounced(`s${uid}`, () => {
      const i = indexOfUid(uid);
      if (i !== -1) commitStart(i, val);
    });
  };
  const onEndSelect = (val, index) => {
    const uid = routes.value[index]?.uid;
    cancelDebounce(`e${uid}`);
    commitEnd(index, val);
  };
  const onEndChange = (val, index) => {
    const uid = routes.value[index]?.uid;
    debounced(`e${uid}`, () => {
      const i = indexOfUid(uid);
      if (i !== -1) commitEnd(i, val);
    });
  };

  // ----- Totali -------------------------------------------------------------------
  const totalPreviewKm = computed(() => {
    let sum = 0;
    // Ogni auto fa la sua strada.
    routes.value.forEach((r) => {
      if (typeof r.previewKm === 'number') sum += r.previewKm * (1 + (r.altreAuto || []).length);
    });
    return Math.round((sum + Number.EPSILON) * 100) / 100;
  });
  const totalPreviewMin = computed(() => {
    let sum = 0;
    routes.value.forEach((r) => {
      if (typeof r.previewMin === 'number') sum += r.previewMin * (1 + (r.altreAuto || []).length);
    });
    return Math.round(sum);
  });
  // Totale solo se TUTTE le corse hanno un costo: un totale parziale
  // sembrerebbe il prezzo dello spostamento, e non lo è (come sul dettaglio).
  // Il costo di una corsa con tutte le sue auto (null se ne manca uno).
  const prezzoCorsa = (r) => {
    if (r.previewPriceState !== 'ok') return null;
    const altre = r.altreAuto || [];
    if (!altre.every((a) => a.previewPriceState === 'ok')) return null;
    return altre.reduce((acc, a) => acc + a.previewPrice, r.previewPrice);
  };
  const totalPreviewPrice = computed(() => {
    if (!routes.value.length) return null;
    if (!routes.value.every((r) => prezzoCorsa(r) !== null)) return null;
    const sum = routes.value.reduce((acc, r) => acc + prezzoCorsa(r), 0);
    return Math.round((sum + Number.EPSILON) * 100) / 100;
  });
  // Perché il totale non c'è, scritto nel riepilogo al posto dell'importo:
  // un totale che sparisce in silenzio si legge come «il costo non esiste».
  // Vuoto = nessuna corsa con i km ancora, oppure totale presente.
  const totalPriceNote = computed(() => {
    const conKm = routes.value.filter((r) => typeof r.previewKm === 'number');
    if (!conKm.length || totalPreviewPrice.value !== null) return '';
    const stati = routes.value.map((r) => r.previewPriceState);
    if (conKm.some((r) => r.previewPriceState === 'idle') && !unref(date)) {
      return 'scegli la data per il costo';
    }
    if (stati.includes('loading')) return 'calcolo del costo…';
    if (stati.includes('error')) return 'costo non disponibile';
    if (stati.includes('missing')) return 'costo non calcolabile: manca una tariffa';
    return 'costo quando tutte le corse hanno partenza e arrivo';
  });

  // Tratta "pronta": tutti i campi OBBLIGATORI compilati (il tipo di cambio è
  // opzionale). Usato per il feedback visivo della card nel builder.
  const isLegComplete = (leg) =>
    !!(
      leg &&
      leg.startPoint &&
      leg.endPoint &&
      leg.dateStart &&
      leg.dateEnd &&
      leg.marca &&
      leg.modello &&
      // Con la targa prova il veicolo non ha ancora una targa sua: non si
      // può pretendere.
      (leg.targa || leg.nonImmatricolato) &&
      !rientroDaScegliere(leg) &&
      statoDi(leg) !== 'scoperto' &&
      (leg.altreAuto || []).every(autoCompleta)
    );

  // ----- Output (contratto col backend: INVARIATO) ---------------------------------
  const validate = ({ ignoreAvailability = false } = {}) => {
    for (let i = 0; i < routes.value.length; i++) {
      const r = routes.value[i];
      const label = `Corsa ${i + 1}`;
      if (!r.startPoint || !r.endPoint) {
        return { ok: false, message: `${label}: punti mancanti` };
      }
      if (oltreMezzanotte(r)) {
        return { ok: false, message: `${label}: con partenza alle ${r.dateStart} arriverebbe dopo mezzanotte` };
      }
      if (!r.dateStart || !r.dateEnd) {
        return { ok: false, message: `${label}: orari non calcolati` };
      }
      // La targa è obbligatoria solo per i veicoli immatricolati: con la
      // targa prova il veicolo non ne ha ancora una sua.
      if (!r.marca || !r.modello || (!r.targa && !r.nonImmatricolato)) {
        return { ok: false, message: `${label}: dati auto mancanti` };
      }
      if (rientroDaScegliere(r)) {
        return { ok: false, message: `${label}: indica con quale corsa rientra il driver` };
      }
      const k = (r.altreAuto || []).findIndex((a) => !autoCompleta(a));
      if (k !== -1) {
        return { ok: false, message: `${label}, auto ${k + 2}: dati auto mancanti` };
      }
    }
    // Nessun driver a piedi (lo controlla anche il server).
    const autoScoperta = autoInPiu.value.find((x) => analisi.value.stato.get(x.id) === 'scoperto');
    if (autoScoperta) {
      const n = routes.value.indexOf(autoScoperta.leg) + 1;
      // Al ritorno più auto che driver arrivati: un'auto resta senza chi la guidi.
      const prima = precedenteDi(autoScoperta.leg);
      if (prima && prima.isShuttle === false && autoScoperta.k >= (prima.altreAuto || []).length) {
        return {
          ok: false,
          message: `Corsa ${n}: al massimo ${1 + (prima.altreAuto || []).length} auto, tante quanti i driver `
            + `arrivati con la corsa ${numeroDi(idDi(prima))}`,
        };
      }
      const motivo = analisi.value.rientriNonValidi.get(autoScoperta.id);
      return {
        ok: false,
        message: motivo && !motivo.includes('non c\'è più')
          ? `Corsa ${n}, auto ${autoScoperta.k + 2}: ${motivo}`
          : `Corsa ${n}: con più auto serve il rientro del driver della prima, a bordo del quale tornano gli altri`,
      };
    }
    const scoperta = routes.value.findIndex((r) => statoDi(r) === 'scoperto');
    if (scoperta !== -1) {
      const luogo = nomeLuogo(routes.value[scoperta].endText) || 'destinazione';
      return {
        ok: false,
        message: `Corsa ${scoperta + 1}: il driver resta a ${luogo} senza rientro. Aggiungi il rientro o scegli come rientra.`,
      };
    }
    // Disponibilità: blocca il salvataggio finché una corsa non è disponibile
    // o la verifica è in corso (salvo forzatura di Simba, solo dall'admin). Se
    // la verifica è fallita decide il server.
    if (!ignoreAvailability) {
      for (let i = 0; i < routes.value.length; i++) {
        const a = routes.value[i].availability;
        if (a?.state === 'loading') {
          return { ok: false, message: 'Verifica della disponibilità in corso: riprova tra un attimo' };
        }
        if (a?.state === 'unavailable') {
          return { ok: false, message: `Corsa ${i + 1}: ${availabilityMessage(routes.value[i])}` };
        }
      }
    }
    return { ok: true, message: '' };
  };

  // Il rientro per il backend: la posizione di una corsa di questo
  // spostamento o l'id di una corsa di un altro.
  // Le corse come le vede il backend: ogni auto in più dopo la sua corsa.
  const espanse = () =>
    routes.value.flatMap((leg) => [{ leg, auto: null }, ...(leg.altreAuto || []).map((auto) => ({ leg, auto }))]);
  const posizioneDto = (id) => espanse().findIndex((e) => !e.auto && idDi(e.leg) === id);
  const versoDto = (su) => {
    if (!su || su === '?') return {};
    if (su.startsWith('x')) return { rientro_route_id: su.slice(1) };
    const i = posizioneDto(su);
    return i === -1 ? {} : { rientro_index: i };
  };
  const rientroDto = (leg) => {
    if (leg.isShuttle !== false || !leg.rientroAltrove) return {};
    return versoDto(corsaDiRientro(leg));
  };
  // L'auto in più: o guida l'auto corrispondente della corsa che prosegue
  // (nessun legame: il backend la accoppia da sé), o rientra a bordo.
  const rientroAutoDto = (leg, auto) => {
    const k = (leg.altreAuto || []).indexOf(auto);
    const andata = andataAuto(leg, k);
    const i = andata ? posizioneDto(andata) : -1;
    return { ...versoDto(rientroAuto(leg, k)), ...(i === -1 ? {} : { andata_index: i }) };
  };

  // La MAPPA della giornata per il disegno: le sedi e gli spostamenti delle
  // auto fra di loro, con orari, auto, targa prova e chi rientra a bordo
  // (vedi utils/catene.js).
  const mappa = computed(() =>
    vistaSedi(
      routes.value.map((leg, i) => ({
        id: idDi(leg),
        n: i + 1,
        da: chiaveLuogo(leg.startPoint),
        a: chiaveLuogo(leg.endPoint),
        daNome: nomeLuogo(leg.startText),
        aNome: nomeLuogo(leg.endText),
        inizio: minuti(leg.dateStart),
        fine: minuti(leg.dateEnd),
        ora: leg.dateStart,
        oraFine: leg.dateEnd,
        catena: leg.isShuttle === false,
        padre: leg.autoChained && leg.chainedFrom ? `u${leg.chainedFrom}` : null,
        // Da scegliere: nel disegno resta scoperto finché non si sceglie.
        rientro: leg.isShuttle === false && leg.rientroAltrove ? leg.rientroSu || '?' : null,
        auto: [leg.marca, leg.modello, leg.targa].filter(Boolean).join(' '),
        prova: !!leg.nonImmatricolato,
      }))
        .concat(autoInPiu.value.map((x) => ({
          id: x.id,
          n: routes.value.indexOf(x.leg) + 1,
          da: x.da,
          a: x.a,
          daNome: nomeLuogo(x.leg.startText),
          aNome: nomeLuogo(x.leg.endText),
          inizio: x.inizio,
          fine: x.fine,
          ora: x.leg.dateStart,
          oraFine: x.leg.dateEnd,
          catena: x.catena,
          rientro: x.rientro,
          andata: x.andata,
          auto: [x.auto.marca, x.auto.modello, x.auto.targa].filter(Boolean).join(' ') || `auto ${x.k + 2}`,
          prova: !!x.auto.nonImmatricolato,
        })))
        .concat(esterne.value.map((e) => ({ ...e, daNome: e.percorso.split(' → ')[0] })))
    )
  );

  const buildRoutesDto = () => {
    const d = unref(date) || '';
    return espanse().map(({ leg: r, auto }) => {
      const [sh, sm] = (r.dateStart || '').split(':').map(Number);
      const [eh, em] = (r.dateEnd || '').split(':').map(Number);
      const ds = new Date(d);
      ds.setHours(sh || 0, sm || 0, 0);
      const de = new Date(d);
      de.setHours(eh || 0, em || 0, 0);
      // In modifica la corsa già salvata dice chi è: si aggiorna sul posto
      // invece di rigenerarla (driver, foto, minuti restano).
      const salvata = auto ? auto.sourceId : r.sourceId;
      return {
        ...(salvata ? { id: salvata } : {}),
        startPoint: pointToDto(r.startPoint),
        endPoint: pointToDto(r.endPoint),
        dateStart: ds.toISOString(),
        dateEnd: de.toISOString(),
        // Ogni auto ha le sue note.
        details: (auto ? auto.details : r.details) || '',
        movement: r.movement,
        // Tipo missione persistito: il dispatcher mostra il campo Jolly solo
        // per gli "Spostamento a 2 operatori".
        two_operators: !!r.isShuttle,
        marca: (auto || r).marca,
        modello: (auto || r).modello,
        targa: (auto || r).targa,
        gearbox_type: (auto || r).gearbox_type || undefined,
        immatricolato: !(auto || r).nonImmatricolato,
        // Come rientra il driver, se non con una corsa che guida lui.
        ...(auto ? rientroAutoDto(r, auto) : rientroDto(r)),
      };
    });
  };

  // ----- Disponibilità (backend) -------------------------------------------------
  // Appena una corsa ha partenza, arrivo e orari, il backend dice se è
  // disponibile: capacità driver della provincia di partenza (contano tutte le
  // corse, di ogni sede, e le altre corse di questo modulo) e driver liberi.
  // Se non lo è propone il primo orario libero. Si verifica tutto il modulo
  // insieme, con un piccolo ritardo, scartando le risposte superate.
  const IDLE = { state: 'idle', reason: null, firstStart: '' };
  const readyForCheck = (r) => !!(r.startPoint && r.endPoint && r.dateStart && r.dateEnd);
  // ⚠️ Per ECCESSO al minuto. Il primo orario libero è un istante preciso e
  // spesso ha i secondi (una corsa occupa fino alla sua fine più il margine:
  // 17:36:20 + 5 min = 17:41:20). Troncando si otterrebbe 17:41:00, cioè un
  // orario ANCORA occupato: la corsa veniva spostata lì e il controllo la
  // rifiutava di nuovo, proponendo lo stesso orario all'infinito.
  const toHHmm = (iso) => {
    const d = new Date(iso);
    const giorno = d.getDate();
    if (d.getSeconds() || d.getMilliseconds()) {
      d.setSeconds(0, 0);
      d.setMinutes(d.getMinutes() + 1);
    }
    // Arrotondando si può scavallare la mezzanotte: un orario del giorno dopo
    // non è proponibile su questa giornata.
    if (d.getDate() !== giorno) return '';
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  };
  // ----- Targhe prova nel modulo -------------------------------------------
  // Le targhe prova sono 3 per tutti. Nel modulo non se ne spuntano più di 3
  // su corse alla stessa ora: la quarta casella non si accende. Quelle già in
  // uso da altri spostamenti le conta il backend (`provaNonDisponibile`).
  const MAX_TARGHE_PROVA = 3;
  const veicoliDelModulo = () => routes.value.flatMap((leg) => [
    { leg, v: leg },
    ...(leg.altreAuto || []).map((a) => ({ leg, v: a })),
  ]);
  const insiemeA = (a, b) => a === b
    || !!(a.dateStart && a.dateEnd && b.dateStart && b.dateEnd && a.dateStart < b.dateEnd && b.dateStart < a.dateEnd);
  // Il veicolo `v` della corsa `leg` può prendere una targa prova?
  const targaProvaLibera = (leg, v) => v.nonImmatricolato
    || veicoliDelModulo().filter((x) => x.v !== v && x.v.nonImmatricolato && insiemeA(x.leg, leg)).length
      < MAX_TARGHE_PROVA;

  let availabilitySeq = 0;
  const checkAvailability = async () => {
    const mine = ++availabilitySeq;
    const day = unref(date);
    const ready = routes.value.filter(readyForCheck);
    routes.value.filter((r) => !readyForCheck(r)).forEach((r) => Object.assign(r.availability, IDLE));
    if (!/^\d{4}-\d{2}-\d{2}$/.test(String(day || '')) || !ready.length) {
      ready.forEach((r) => Object.assign(r.availability, IDLE));
      return;
    }
    ready.forEach((r) => Object.assign(r.availability, { state: 'loading', reason: null, firstStart: '' }));
    // Ogni auto è una corsa e occupa un driver: si verificano tutte, e la
    // corsa è disponibile solo se lo sono tutte le sue auto.
    const dto = buildRoutesDto();
    const voci = espanse()
      .map((e, i) => ({ leg: e.leg, veicolo: e.auto || e.leg, d: dto[i], i }))
      .filter((v) => readyForCheck(v.leg));
    // Il rientro a bordo è una posizione fra le corse del salvataggio: qui si
    // mandano solo quelle pronte, quindi va riportata a questo elenco.
    const posizione = new Map(voci.map((v, k) => [v.i, k]));
    const legsPayload = voci.map(({ d }) => ({
      startPoint: d.startPoint, dateStart: d.dateStart, dateEnd: d.dateEnd, two_operators: d.two_operators,
      // Targa prova: le targhe sono 3 per tutti, il backend le conta.
      immatricolato: d.immatricolato,
      // Arrivo e rientro: il backend ci riconosce le catene, e chi prosegue
      // una catena non chiede un driver nuovo (lo guida chi è già arrivato).
      endPoint: d.endPoint,
      ...(posizione.has(d.rientro_index) ? { rientro_index: posizione.get(d.rientro_index) } : {}),
      ...(posizione.has(d.andata_index) ? { andata_index: posizione.get(d.andata_index) } : {}),
    }));
    try {
      const res = await journeyAPI.checkAvailability(legsPayload, unref(journeyId) || undefined);
      if (mine !== availabilitySeq) return;
      const esito = new Map();
      veicoliDelModulo().forEach(({ v }) => { v.provaNonDisponibile = false; });
      (res.data?.legs || []).forEach((l) => {
        const voce = voci[l.index];
        const r = voce?.leg;
        if (!r) return;
        // Quale auto è rimasta senza targa prova: lo si dice sull'auto.
        voce.veicolo.provaNonDisponibile = !l.available && l.reason === 'targa_prova';
        // La prima auto non disponibile decide l'orario proposto; i motivi
        // si raccolgono tutti, se no uno nasconde l'altro (la capacità della
        // provincia copriva le targhe prova finite).
        const prima = esito.get(r);
        if (prima?.state === 'unavailable') {
          if (!l.available && l.reason && !prima.reasons.includes(l.reason)) prima.reasons.push(l.reason);
          return;
        }
        esito.set(r, {
          state: l.available ? 'ok' : 'unavailable',
          reason: l.reason,
          reasons: l.available || !l.reason ? [] : [l.reason],
          firstStart: l.first_available_start ? toHHmm(l.first_available_start) : '',
        });
      });
      esito.forEach((v, r) => Object.assign(r.availability, v));
    } catch (e) {
      if (mine !== availabilitySeq) return;
      ready.forEach((r) => Object.assign(r.availability, { state: 'error', reason: null, firstStart: '' }));
    }
  };
  const debouncedAvailability = debounce(checkAvailability, 400);
  const availabilitySignature = computed(() =>
    [
      unref(date) || '',
      ...routes.value.map((r) =>
        [
          pointKey(r.startPoint), r.startPoint?.province || '', pointKey(r.endPoint), r.dateStart, r.dateEnd, r.isShuttle,
          // quante auto e quali con la targa prova
          [r.nonImmatricolato, ...(r.altreAuto || []).map((a) => a.nonImmatricolato)].map((x) => (x ? 'p' : 'n')).join(''),
        ].join('~')
      ),
    ].join('>>')
  );
  watch(availabilitySignature, () => debouncedAvailability(), { immediate: true });
  // Cambiata la data, gli esiti di prima non valgono più: spariscono subito
  // (e la verifica riparte), invece di restare finché non arriva la risposta.
  watch(
    () => unref(date),
    () => {
      routes.value.forEach((r) => Object.assign(r.availability, readyForCheck(r)
        ? { state: 'loading', reason: null, firstStart: '' }
        : IDLE));
    }
  );

  // Il messaggio per chi compila: non dice quali corse occupano l'orario
  // (possono essere di altre sedi o di altri partner).
  // Il motivo arriva dal backend: driver o jolly già impegnati, oppure il
  // tetto di corse insieme della provincia. Dire sempre «driver» nascondeva
  // gli altri due casi, che si risolvono in modo diverso.
  const MOTIVO = {
    drivers: 'non ci sono driver liberi',
    jolly: 'non c\'è un jolly libero',
    driver_capacity: 'la provincia ha già il massimo di corse insieme',
    jolly_capacity: 'la provincia ha già il massimo di spostamenti singoli',
    targa_prova: 'sono già in uso tutte le targhe prova (sono 3)',
  };
  const availabilityMessage = (leg) => {
    const a = leg?.availability;
    if (!a || a.state !== 'unavailable') return '';
    // Un «primo orario libero» uguale a quello già impostato non è
    // un'alternativa: dirlo evita di far sembrare che ci sia un rimedio.
    const alternativa = a.firstStart && a.firstStart !== leg.dateStart;
    const next = alternativa
      ? `primo orario libero ${a.firstStart}`
      : 'nessun altro orario libero in giornata';
    const motivi = (a.reasons?.length ? a.reasons : [a.reason]).map((x) => MOTIVO[x] || 'non ci sono driver liberi');
    const motivo = [...new Set(motivi)].join(' e ');
    return `alle ${leg.dateStart} ${motivo} (${next})`;
  };

  // «Usa HH:mm»: sposta la partenza al primo orario libero; da lì ripartono il
  // calcolo dell'arrivo, lo slittamento delle corse successive e la verifica.
  //
  // ⚠️ La verifica riparte SEMPRE a mano, non basta cambiare l'orario: se il
  // nuovo orario è uguale a quello di prima (il backend a volte propone lo
  // stesso orario), oppure se la cascata degli orari lo riporta al minimo
  // consentito, la firma non cambia, il watcher non scatta e il messaggio di
  // «non disponibile» resterebbe lì per sempre.
  const useFirstStart = (index) => {
    const leg = routes.value[index];
    if (!leg?.availability?.firstStart) return;
    leg.dateStart = leg.availability.firstStart;
    calculateArrivalTime(routes.value[0], 0);
    Object.assign(leg.availability, { state: 'loading', reason: null, firstStart: '' });
    debouncedAvailability();
  };

  const buildJourneyPayload = (partner) => ({
    date: unref(date),
    partner_id: partner?.id,
    employee_id: storeLogin.employee?.id,
    routes: buildRoutesDto(),
  });

  return {
    // stato
    routes,
    headquartersCascader,
    totalPreviewKm,
    totalPreviewMin,
    totalPreviewPrice,
    totalPriceNote,
    // punti
    querySuggestions,
    onStartSelect,
    onStartChange,
    onEndSelect,
    onEndChange,
    // orari
    calculateArrivalTime,
    minimoPartenza,
    getDisabledHours,
    getDisabledMinutes,
    defaultStartTime,
    oltreMezzanotte,
    // chaining
    setShuttle,
    soloCatena,
    notaCorsa,
    opzioniRientro,
    scegliRientro,
    scegliComeRientra,
    puoRientrareAltrove,
    rientroDaScegliere,
    aggiungiAuto,
    puoAggiungereAuto,
    targaProvaLibera,
    MAX_TARGHE_PROVA,
    togliAuto,
    prezzoCorsa,
    notaAuto,
    MAX_AUTO,
    mappa,
    aggiungiRientro,
    addRouteAtEnd,
    insertRouteAfter,
    canInsertAfter,
    removeRoute,
    canRemove,
    // disponibilità
    availabilityMessage,
    useFirstStart,
    // stato tratta
    isLegComplete,
    // motore
    recomputeFrom,
    // output
    validate,
    buildRoutesDto,
    buildJourneyPayload,
  };
}
